import React, { useEffect, useState } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { subscriptionsApi } from '../api/client';
import { ONBOARDING_PATH } from '../utils/onboarding';
import { resolveOnboardingGateStatus } from '../utils/waitForOnboardingComplete';

interface OnboardingGateProps {
  children: React.ReactNode;
}

function isAdminFromToken(): boolean {
  try {
    const token = localStorage.getItem('authToken');
    if (!token) return false;
    const payload = JSON.parse(atob(token.split('.')[1]));
    return Boolean(payload.isAdmin);
  } catch {
    return false;
  }
}

function isReturningFromSetupCheckout(search: string): boolean {
  return new URLSearchParams(search).get('setup') === 'complete';
}

/**
 * Blocks dashboard/editor routes until new users finish payment onboarding.
 * Admins bypass the API check. Fails closed if the onboarding API is unreachable.
 * After Stripe setup checkout, polls briefly for the webhook to complete onboarding.
 */
export const OnboardingGate: React.FC<OnboardingGateProps> = ({ children }) => {
  const location = useLocation();
  const [checking, setChecking] = useState(true);
  const [requiresPaymentSetup, setRequiresPaymentSetup] = useState(true);
  const [awaitingCardConfirmation, setAwaitingCardConfirmation] = useState(false);
  const [confirmationAttempt, setConfirmationAttempt] = useState(0);
  const pollAfterSetupCheckout = isReturningFromSetupCheckout(location.search);

  useEffect(() => {
    if (isAdminFromToken()) {
      setRequiresPaymentSetup(false);
      setAwaitingCardConfirmation(false);
      setChecking(false);
      return;
    }

    let cancelled = false;
    setChecking(true);
    setAwaitingCardConfirmation(false);

    resolveOnboardingGateStatus(() => subscriptionsApi.getOnboardingStatus(), {
      pollAfterSetupCheckout,
    })
      .then(result => {
        if (!cancelled) {
          setRequiresPaymentSetup(result.requiresPaymentSetup);
          setAwaitingCardConfirmation(pollAfterSetupCheckout && result.requiresPaymentSetup);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setChecking(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [pollAfterSetupCheckout, confirmationAttempt]);

  if (checking) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto" />
          <p className="mt-4 text-gray-600">
            {pollAfterSetupCheckout ? 'Finishing setup...' : 'Loading your account...'}
          </p>
        </div>
      </div>
    );
  }

  if (requiresPaymentSetup && awaitingCardConfirmation) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow p-8 text-center space-y-4">
          <h1 className="text-xl font-semibold text-gray-900">We're still confirming your card</h1>
          <p className="text-sm text-gray-600">
            This can take a moment. Try again, or enter your card again.
          </p>
          <button
            type="button"
            onClick={() => setConfirmationAttempt(attempt => attempt + 1)}
            className="w-full py-2 px-4 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700"
          >
            Try again
          </button>
          <Link to={ONBOARDING_PATH} className="block text-sm text-blue-600 hover:text-blue-800">
            Enter your card again
          </Link>
        </div>
      </div>
    );
  }

  if (requiresPaymentSetup) {
    return <Navigate to={ONBOARDING_PATH} replace />;
  }

  return <>{children}</>;
};
